<script setup lang="ts">
/**
 * Bottom navigation pour mobile et tablette (cachee >= lg).
 *
 * Pattern UX standard pour les apps mobiles : les actions de navigation
 * principales sont en bas, au pouce, plus accessibles que le header.
 *
 * Affiche jusqu'a 5 boutons selon le role :
 *   - Accueil
 *   - Chantiers
 *   - Pointages (cache pour architecte / client_final)
 *   - Fiches d'heures (idem)
 *   - Branding (super_admin_sds uniquement)
 *
 * Highlight l'onglet de la route courante (barre top accent + texte color).
 */
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Home, HardHat, Clock, Briefcase, Sliders } from 'lucide-vue-next';
import { useAuthStore } from '../stores/auth';

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const auth = useAuthStore();

const role = computed(() => auth.user?.role);

/**
 * Onglet "Temps" unifie (Pointages + Fiches d'heures).
 * Tous les roles vont sur /temps. TempsView adapte le contenu du tab
 * "Pointages" selon le role (PointageView pour compagnon, PointagesListView
 * pour management).
 */
const canSeeTemps = computed(() => {
  const r = role.value;
  return r === 'super_admin_sds' || r === 'admin' || r === 'direction'
      || r === 'conducteur' || r === 'chef' || r === 'compagnon';
});

/**
 * "Accueil" cache pour le compagnon : il n'a pas de dashboard utile
 * (KPI tenant cachees, redirect automatique vers /temps). Le retirer de
 * la nav evite un onglet qui ne fait rien d'utile pour lui.
 */
const canSeeHome = computed(() => role.value !== 'compagnon');

const canSeeBranding = computed(() => role.value === 'super_admin_sds');

/**
 * Onglet unifie "Gestion" qui regroupe Demandes / Vehicules / Paie en
 * sous-tabs (refonte 2026-05-31 pour aerer la BottomNav). Sous-tab Paie
 * filtre automatiquement dans GestionView selon le role.
 */
const canSeeGestion = computed(() => {
  const r = role.value;
  return r === 'super_admin_sds' || r === 'admin' || r === 'direction'
      || r === 'conducteur' || r === 'chef' || r === 'compagnon';
});

const items = computed(() => {
  const out: { key: string; label: string; icon: typeof Home; route: string; activePrefixes: string[] }[] = [];
  if (canSeeHome.value) {
    out.push({ key: 'home', label: t('common.nav.home'), icon: Home, route: 'home', activePrefixes: ['home'] });
  }
  out.push({ key: 'chantiers', label: t('chantiers.page_title'), icon: HardHat, route: 'chantiers-list', activePrefixes: ['chantier'] });
  if (canSeeTemps.value) {
    out.push({
      key: 'temps',     label: t('temps.page_title'),         icon: Clock,
      route: 'temps',
      // Match aussi les anciennes routes (pointages-list, fiches-heures-list)
      // qui redirigent maintenant vers /temps.
      activePrefixes: ['temps', 'pointage', 'fiches-heures'],
    });
  }
  if (canSeeGestion.value) {
    out.push({
      key: 'gestion',  label: t('gestion.page_title'),        icon: Briefcase,
      route: 'gestion',
      // Match aussi les anciennes routes (demandes-list, vehicules-list,
      // paie-settings) qui redirigent maintenant vers /gestion + vue detail
      // vehicules et "Qui conduisait" qui restent sous le scope Gestion.
      activePrefixes: ['gestion', 'demandes', 'vehicule', 'qui-conduisait', 'paie-settings'],
    });
  }
  if (canSeeBranding.value) {
    out.push({
      key: 'branding',  label: 'Branding',                    icon: Sliders,
      route: 'sds-branding',                                   activePrefixes: ['sds-branding'],
    });
  }
  return out;
});

function isActive(prefixes: string[]): boolean {
  const name = String(route.name ?? '');
  return prefixes.some((p) => name === p || name.startsWith(p));
}

function go(routeName: string): void {
  router.push({ name: routeName });
}
</script>

<template>
  <!-- Cache sur >= lg (1024px+) : la nav header suffit sur desktop -->
  <nav
    class="lg:hidden fixed bottom-0 inset-x-0 z-sticky bg-card border-t border-border"
    :aria-label="t('common.nav.menu')"
  >
    <ul class="flex items-stretch max-w-6xl mx-auto safe-bottom">
      <li v-for="item in items" :key="item.key" class="flex-1">
        <button
          type="button"
          :class="[
            'relative w-full flex flex-col items-center justify-center gap-1 py-2 px-1 transition-colors',
            'min-h-[56px]',
            isActive(item.activePrefixes)
              ? 'text-accent'
              : 'text-text-secondary hover:text-text active:bg-bg-muted',
          ]"
          :aria-current="isActive(item.activePrefixes) ? 'page' : undefined"
          @click="go(item.route)"
        >
          <span
            v-if="isActive(item.activePrefixes)"
            class="absolute top-0 inset-x-3 h-0.5 bg-accent rounded-b-full"
            aria-hidden="true"
          ></span>
          <component :is="item.icon" :size="20" aria-hidden="true" />
          <span class="text-[10px] font-medium leading-none truncate w-full text-center">{{ item.label }}</span>
        </button>
      </li>
    </ul>
  </nav>
</template>

<style scoped>
/* Respect du safe-area iOS (encoche bas + barre home indicator). */
.safe-bottom {
  padding-bottom: env(safe-area-inset-bottom, 0);
}
</style>
